/**
 * ManualDMInput types and helper functions
 *
 * For data fetching and mutations, see: src/model/useManualReadingsQueries.ts
 */

export enum EventType {
  GrazingEvent = "GrazingEvent",
  PartialGrazingEvent = "PartialGrazingEvent",
  SilageEvent = "SilageEvent",
  None = "None",
}

export type InputKind = "user" | "machine";
export type InputVerificationStatus = "Proposed" | "Accepted" | "Rejected";

export interface Event {
  eventType: EventType;
  dateTimeIn?: Date;
  dateTimeOut?: Date;
  dateTimeCut?: Date;
  preEventCover?: number;
  postEventCover?: number;
  // Optional on update: attach an animal group to an event that has none.
  // Backend resolves to AnimalGroupHistory active at dateTimeIn (or earliest).
  // Rejected if the event already has an animal group.
  animalGroupGuid?: string;
}

export interface ManualDMInputItem {
  dryMatterValue: number;
  newDryMatterValue?: number;
  field: string;
  fieldName?: string;
  dateTimeMeasurementStart?: Date;
  dateTimeMeasurementEnd?: Date;
  event: Event;
  kind: InputKind;
  verificationStatus: InputVerificationStatus;
  version?: string;
}

export interface ManualDMInput {
  guid: string;
  dryMatterMachineEstimate?: number;
  manualDryMatterInput: ManualDMInputItem;
}

export interface EventResponse {
  eventType: EventType;
  dateTimeIn?: string;
  dateTimeOut?: string;
  dateTimeCut?: string;
  preEventCover?: number;
  postEventCover?: number;
  movedToShed?: boolean;
  animalGroupGuid?: string;
  // Populated when the request was made with `include_animal_groups=true`.
  // `guid` is the parent AnimalGroup guid (history rows share their parent guid).
  animalGroup?: { guid: string; name?: string } | null;
  animalGroupName?: string | null;
}

export interface ManualDMInputResponseItem {
  dryMatterValue?: number;
  field: string;
  dateTimeMeasurementStart: string;
  dateTimeMeasurementEnd: string;
  event: EventResponse;
  kind: InputKind;
  verificationStatus: InputVerificationStatus;
  version?: string;
}

export interface ManualDMInputResponse {
  guid: string;
  dryMatterMachineEstimate?: number;
  manualDryMatterInput: ManualDMInputResponseItem;
}

export interface BulkResponse {
  data: Array<ManualDMInputResponse>;
  meta?: {
    nextCursor?: string | null;
    nextPage?: string | null;
  };
}
